import { rootApi } from "../../app/apis/api.root";
import { API_TAGS } from "../../app/constants";
import { API_URL } from "../../constants/urls.constants";
import { CommonResponse, GetQueryArgsType } from "../../types";
import {
  BusinessUnitList,
  DepartmentList,
  InvoiceDetails,
  InvoiceList,
  InvoiceListParams,
} from "./invoice.type";

const { INVOICE_LIST, BUSINESS_UNIT_LIST, DEPARTMENT_LIST } = API_TAGS || {};

export const invoiceApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    // Mutations
    createInvoice: builder.mutation({
      query: (data) => {
        return {
          url: API_URL.createInvoice,
          method: "POST",
          body: data,
        };
      },
      invalidatesTags: [INVOICE_LIST],
    }),

    updateInvoice: builder.mutation({
      query: ({ data, id }) => {
        return {
          url: `${API_URL.updateInvoice}/${id}`,
          method: "PATCH",
          body: data,
        };
      },
      invalidatesTags: (_, __, id) => [
        INVOICE_LIST,
        { type: INVOICE_LIST, id },
      ],
    }),

    archiveInvoice: builder.mutation({
      query: ({ id }) => {
        return {
          url: `${API_URL.archiveInvoice}/${id}`,
          method: "PATCH",
        };
      },
      invalidatesTags: (_, __, id) => [
        INVOICE_LIST,
        { type: INVOICE_LIST, id },
      ],
    }),

    sendToApprover: builder.mutation({
      query: ({ id }) => {
        return {
          url: `${API_URL.sendToApprover}/${id}`,
          method: "PATCH",
        };
      },
      invalidatesTags: (_, __, id) => [
        INVOICE_LIST,
        { type: INVOICE_LIST, id },
      ],
    }),

    approveInvoice: builder.mutation({
      query: ({ id, data }) => {
        return {
          url: `${API_URL.approveInvoice}/${id}`,
          method: "PATCH",
          body: data,
        };
      },
      invalidatesTags: (_, __, id) => [
        INVOICE_LIST,
        { type: INVOICE_LIST, id },
      ],
    }),

    rejectInvoice: builder.mutation({
      query: ({ id, data }) => {
        return {
          url: `${API_URL.rejectInvoice}/${id}`,
          method: "PATCH",
          body: data,
        };
      },
      invalidatesTags: (_, __, id) => [
        INVOICE_LIST,
        { type: INVOICE_LIST, id },
      ],
    }),

    sendToDisbursement: builder.mutation({
      query: ({ id, data }) => {
        return {
          url: `${API_URL.sendToDisbursement}`,
          method: "POST",
          body: data,
        };
      },
      invalidatesTags: (_, __, id) => [
        INVOICE_LIST,
        { type: INVOICE_LIST, id },
      ],
    }),

    // Queries
    getInvoiceList: builder.query<
      CommonResponse<InvoiceList>,
      GetQueryArgsType<InvoiceListParams>
    >({
      query: (queryArgs) => ({
        url: API_URL.getInvoiceList,
        ...queryArgs,
      }),
      providesTags: [INVOICE_LIST],
    }),

    getInvoiceDetails: builder.query<CommonResponse<InvoiceDetails>, number>({
      query: (id) => ({
        url: `${API_URL.getInvoiceDetails}/${id}`,
      }),
      providesTags: (_, __, id) => [{ type: INVOICE_LIST, id }],
    }),

    getBusinessUnitList: builder.query<CommonResponse<BusinessUnitList>, {}>({
      query: () => ({
        url: `${API_URL.getBusinessUnitList}`,
      }),
      providesTags: [BUSINESS_UNIT_LIST],
    }),

    getDepartmentList: builder.query<CommonResponse<DepartmentList>, {}>({
      query: () => ({
        url: `${API_URL.getDepartmentList}`,
      }),
      providesTags: [DEPARTMENT_LIST],
    }),

    getWaitingApprovalInvoiceList: builder.query<
      CommonResponse<InvoiceList>,
      GetQueryArgsType<InvoiceListParams>
    >({
      query: (queryArgs) => ({
        url: `${API_URL.getWaitingForApprovalInvoiceList}`,
        ...queryArgs,
      }),
      providesTags: [INVOICE_LIST],
    }),

    getPaymentsInvoiceList: builder.query<
      CommonResponse<InvoiceList>,
      GetQueryArgsType<InvoiceListParams>
    >({
      query: (queryArgs) => ({
        url: `${API_URL.getPaymentsInvoiceList}`,
        ...queryArgs,
      }),
      providesTags: [INVOICE_LIST],
    }),
  }),
});

export const {
  // Mutations
  useCreateInvoiceMutation,
  useUpdateInvoiceMutation,
  useArchiveInvoiceMutation,
  useSendToApproverMutation,
  useApproveInvoiceMutation,
  useRejectInvoiceMutation,
  useSendToDisbursementMutation,

  // Queries
  useGetInvoiceListQuery,
  useGetInvoiceDetailsQuery,
  useGetBusinessUnitListQuery,
  useGetDepartmentListQuery,
  useGetWaitingApprovalInvoiceListQuery,
  useGetPaymentsInvoiceListQuery,
} = invoiceApi;
